// gate-approval-directive-processing.ts
//
// Bug J (P15): the missing consumer for `approve` directives. Measured
// before this file existed:
//   - `avatar autopilot approve g0 --request <id>` wrote a directive with
//     `kind: "approve"` into the inbox (autopilot-control-directive-
//     commands.ts) and returned a receipt, so it LOOKED like it worked.
//   - The Mattermost thread parser produced the same `kind: "approve"`
//     (mattermost-command-body-parser.ts) and directive-schema.ts validated
//     it.
//   - Nothing consumed it. supervisor-directive-processing.ts handles only
//     REDIRECT_LIKE_KINDS (= {redirect, revert} as of P4 2026-09-16, which
//     moved `answer` onto its own drainer — see
//     interview-answer-directive-processing.ts); supervisor-run-until-idle.ts
//     recognises only pause/abort. `approveGate` had one caller (the G3
//     release path); `approveG3` even carried the comment "called by the
//     directive processor" while no processor called it.
//   => All four gates G0-G3 had NO working approval path, which is also why
//      Bug I (gates never checked at dispatch) stayed invisible: the gate
//      machinery was inert in both directions simultaneously.
//
// Design: this module ROUTES to `approveGate` and never writes gates.json
// itself, so the existing actor-binding / request-id / idempotency rules
// keep applying unchanged. It is called once per tick boundary from
// supervisor-tick-boundary-directive-drain.ts, alongside the answer and
// redirect/revert drains, so an approval that lands mid-run takes effect on the next tick
// without restarting `run`.
import {
  type DelegatedG3Scope,
  rejectOutOfScopeDelegatedG3,
} from "./delegated-g3-directive-scope-check.js";
import {
  isDirectiveProcessed,
  listInboxDirectiveIds,
  markDirectiveProcessed,
  readDirective,
} from "./directive-inbox.js";
import { gateApprovedJournalEntry } from "./gate-approved-journal-event.js";
import { approveGate, type GateId, GateRejectedError } from "./gate-registry.js";
import { isBoundActor, readMissionActorBinding } from "./mission-actor-binding.js";
import type { MissionStore } from "./mission-store.js";

const APPROVABLE_GATES: readonly string[] = ["g0", "g1", "g2", "g3"];

export interface AppliedGateApproval {
  directiveId: string;
  gate: GateId;
  requestId: string;
  actorId: string;
}

export interface RejectedGateApproval {
  directiveId: string;
  /** Human-readable, safe to journal/print — never contains a secret. */
  reason: string;
}

export interface ProcessPendingGateApprovalDirectivesInput {
  store: MissionStore;
  controlRoot: string;
  now?: () => Date;
  /** 06k C7b: phạm vi G3 ủy nhiệm; vắng ⇒ directive `chat_delegated` g3 bị từ chối. */
  delegatedG3Scope?: DelegatedG3Scope;
}

export interface ProcessPendingGateApprovalDirectivesResult {
  applied: AppliedGateApproval[];
  rejected: RejectedGateApproval[];
}

/**
 * Applies every unprocessed `approve` directive in the inbox. Non-approve
 * kinds are left strictly untouched for their own handlers (consuming a
 * `pause` here would swallow the pause).
 *
 * A rejected directive is MARKED PROCESSED: it is a decided outcome, not a
 * transient failure, so retrying it every tick forever would just re-log the
 * same rejection. The directive file itself stays as the durable record of
 * what was asked.
 */
export async function processPendingGateApprovalDirectives(
  input: ProcessPendingGateApprovalDirectivesInput,
): Promise<ProcessPendingGateApprovalDirectivesResult> {
  const applied: AppliedGateApproval[] = [];
  const rejected: RejectedGateApproval[] = [];
  const now = input.now ?? (() => new Date());

  const directiveIds = await listInboxDirectiveIds(input.controlRoot);
  // Deterministic order so two approvals filed in one batch apply in a stable
  // sequence (ids are content hashes — stable, NOT chronological; review L9).
  for (const directiveId of [...directiveIds].sort()) {
    const directive = await readDirective(input.controlRoot, directiveId);
    if (directive === undefined) continue;
    if (directive.kind !== "approve") continue;
    if (await isDirectiveProcessed(input.controlRoot, directiveId)) continue;

    const gate = directive.payload.gate;
    const requestId = directive.payload.request_id;
    if (typeof gate !== "string" || !APPROVABLE_GATES.includes(gate)) {
      rejected.push({
        directiveId,
        reason: `payload.gate không hợp lệ (${JSON.stringify(gate)}) — phải là g0|g1|g2|g3.`,
      });
      await markDirectiveProcessed(input.controlRoot, directiveId, now);
      continue;
    }
    if (typeof requestId !== "string" || requestId.length === 0) {
      rejected.push({
        directiveId,
        reason: `payload.request_id thiếu hoặc rỗng cho gate ${gate} — không tự chọn revision.`,
      });
      await markDirectiveProcessed(input.controlRoot, directiveId, now);
      continue;
    }
    if (await rejectOutOfScopeDelegatedG3(directive, input, rejected, now)) continue;

    // Fail CLOSED on an unknown bound identity: trusting the directive's own
    // actor_id here would let any actor self-authorise, defeating the whole
    // point of the binding (see mission-actor-binding.ts).
    const binding = await readMissionActorBinding(input.controlRoot);
    if (binding === undefined) {
      rejected.push({
        directiveId,
        reason:
          "không đọc được actor binding của mission — danh tính được cấp quyền KHÔNG xác định " +
          "nên từ chối duyệt (fail-closed). KHÔNG dùng actor_id trong directive làm thay.",
      });
      await markDirectiveProcessed(input.controlRoot, directiveId, now);
      continue;
    }

    // P19b: the binding holds BOTH identities of the mission owner (OS
    // account and Mattermost user id) because the two directive producers
    // write `actor_id` in different namespaces. Resolve which one this
    // directive used and hand approveGate that identity, so its own
    // actor-binding rule keeps applying UNCHANGED (equality against a
    // single bound value) rather than being relaxed inside the gate.
    // An actor matching neither identity never reaches approveGate.
    const boundActorId = isBoundActor(binding, directive.actor_id)
      ? directive.actor_id
      : binding.os_account;

    // E1/H1: phạm vi quyền người duyệt cấp kèm theo lệnh approve. Chỉ nhận
    // khi ĐÚNG hình dạng — payload sai kiểu bị BỎ QUA (không ghi rác vào bản
    // ghi quyền), chứ không suy diễn. `expires_at` không parse được vẫn ghi:
    // `deployment-adapter.ts` fail-CLOSED trên giá trị không đọc được, nên
    // chặn deploy là hành vi đúng, an toàn hơn im lặng bỏ hạn.
    const rawTargets = directive.payload.targets;
    const grantTargets =
      Array.isArray(rawTargets) && rawTargets.every((t) => typeof t === "string" && t.length > 0)
        ? (rawTargets as string[])
        : undefined;
    const rawExpires = directive.payload.expires_at;
    const grantExpiresAt =
      typeof rawExpires === "string" && rawExpires.length > 0 ? rawExpires : undefined;

    try {
      const receipt = await approveGate(input.controlRoot, {
        gate: gate as GateId,
        requestId,
        actorId: directive.actor_id,
        boundActorId,
        now,
        ...(grantTargets !== undefined ? { targets: grantTargets } : {}),
        ...(grantExpiresAt !== undefined ? { expiresAt: grantExpiresAt } : {}),
        ...(directive.source === "chat_delegated" ? { source: directive.source } : {}),
      });
      // Journal the authority event itself (not just the gates.json write),
      // so `status` and any audit can answer "who approved what, when" from
      // the durable log. `reused: true` means an earlier identical approval
      // already stood — no new authority was granted, so nothing to journal.
      if (!receipt.reused) {
        await input.store.commit([gateApprovedJournalEntry(directive, gate, requestId, input)]);
      }
      applied.push({ directiveId, gate: gate as GateId, requestId, actorId: directive.actor_id });
    } catch (err) {
      // GateRejectedError is the EXPECTED path for a wrong actor or a stale
      // request id — a decided outcome, so it is recorded and the loop moves on.
      if (!(err instanceof GateRejectedError)) {
        // Audit 2026-09-14 (F6): everything else used to be swallowed here too,
        // the directive marked processed, and the NEXT directive attempted. The
        // audit measured the result: G0 recorded `rejected` because the journal
        // write failed, G1 recorded `applied`, and gates.json ended up showing
        // BOTH approved while the store still said READY.
        //
        // A journal/store failure means we can no longer say who approved what.
        // Half-granted authority is worse than none, so the whole pass stops and
        // the directive is deliberately left UNPROCESSED — a later run retries
        // it once the underlying problem is fixed, instead of burying it.
        throw err;
      }
      rejected.push({ directiveId, reason: err.message });
    }
    await markDirectiveProcessed(input.controlRoot, directiveId, now);
  }

  return { applied, rejected };
}
